import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Inbox, ClipboardList, Plane, RefreshCw } from "lucide-react";
import { adminLogin, adminLogout, adminSession, adminSummary, adminLeads, adminOnboarding } from "@/lib/api";
import { BroadcastControl } from "@/components/BroadcastControl";

const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      onLogin();
      toast.success("Bienvenido al panel");
    } catch (err) {
      toast.error("Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6">
      <form
        onSubmit={submit}
        data-testid="panel-login-form"
        className="w-full max-w-sm rounded-[2rem] bg-cream p-8 shadow-2xl"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-coral">
            <Plane className="h-4 w-4 text-white" strokeWidth={2.5} />
          </span>
          <span className="font-display text-lg font-extrabold tracking-tight text-ink">
            Panel · Anto<span className="text-coral">&</span>Bella
          </span>
        </div>
        <p className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-ink/50">
          <Lock className="h-3.5 w-3.5" /> Acceso restringido
        </p>
        <input
          data-testid="panel-email"
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-3 w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-ink outline-none focus:ring-2 focus:ring-coral"
        />
        <input
          data-testid="panel-password"
          type="password"
          required
          placeholder="Contraseña"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-3 w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-ink outline-none focus:ring-2 focus:ring-coral"
        />
        <button
          data-testid="panel-login-submit"
          type="submit"
          disabled={loading}
          className="mt-5 w-full rounded-full bg-coral px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-70"
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
    <Icon className="h-6 w-6 text-coral" />
    <div className="mt-4 font-display text-4xl font-extrabold tracking-tighter text-ink">{value}</div>
    <div className="mt-1 font-mono text-xs uppercase tracking-widest text-ink/50">{label}</div>
  </div>
);

const Table = ({ rows, columns }) => (
  <div className="overflow-x-auto rounded-3xl bg-white shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-ink/10">
          {columns.map((c) => (
            <th key={c.key} className="whitespace-nowrap px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-ink/40">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="px-4 py-8 text-center text-ink/40">
              Sin registros todavía
            </td>
          </tr>
        )}
        {rows.map((r, i) => (
          <tr key={r.id || i} className="border-b border-ink/5 last:border-0 hover:bg-mist/50">
            {columns.map((c) => (
              <td key={c.key} className="px-4 py-3 align-top text-ink/80">
                {c.render ? c.render(r) : r[c.key] || "—"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("leads");
  const [summary, setSummary] = useState({ leads: 0, onboarding: 0 });
  const [leads, setLeads] = useState([]);
  const [onboarding, setOnboarding] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, o] = await Promise.all([
        adminSummary(),
        adminLeads(),
        adminOnboarding(),
      ]);
      setSummary(s);
      setLeads(l.items);
      setOnboarding(o.items);
    } catch (err) {
      if (err.message === "unauthorized") {
        toast.error("Sesión expirada");
        onLogout();
      } else {
        toast.error("No se pudo cargar la información");
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const leadCols = [
    { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
    { key: "source", label: "Origen" },
    { key: "name", label: "Nombre" },
    { key: "email", label: "Email" },
    { key: "combo", label: "Combo" },
    {
      key: "fields",
      label: "Detalle",
      render: (r) => (r.fields || []).map((f) => `${f.label}: ${f.value}`).join(" · ") || "—",
    },
  ];
  const onbCols = [
    { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
    { key: "kind", label: "Tipo" },
    { key: "name", label: "Contacto" },
    { key: "email", label: "Email" },
    { key: "company", label: "Empresa/Marca" },
    {
      key: "answers",
      label: "Respuestas",
      render: (r) => (
        <span className="line-clamp-3 block max-w-md text-xs">
          {(r.answers || []).map((a) => `${a.label} → ${a.value}`).join(" | ")}
        </span>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="panel-dashboard">
      <header className="flex items-center justify-between border-b border-ink/10 bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-coral">
            <Plane className="h-4 w-4 text-white" strokeWidth={2.5} />
          </span>
          <span className="font-display text-lg font-extrabold tracking-tight text-ink">
            Panel de leads
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="panel-refresh"
            className="inline-flex items-center gap-2 rounded-full bg-mist px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-mint/40"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
          <button
            onClick={onLogout}
            data-testid="panel-logout"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5"
          >
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="grid grid-cols-2 gap-4 sm:max-w-md">
          <StatCard icon={Inbox} label="Contactos" value={summary.leads} />
          <StatCard icon={ClipboardList} label="Onboardings" value={summary.onboarding} />
        </div>

        <BroadcastControl />

        <div className="mt-8 flex gap-2">
          <button
            onClick={() => setTab("leads")}
            data-testid="panel-tab-leads"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "leads" ? "bg-ink text-cream" : "bg-white text-ink/60 hover:text-ink"
            }`}
          >
            Contactos ({summary.leads})
          </button>
          <button
            onClick={() => setTab("onboarding")}
            data-testid="panel-tab-onboarding"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "onboarding" ? "bg-ink text-cream" : "bg-white text-ink/60 hover:text-ink"
            }`}
          >
            Onboardings ({summary.onboarding})
          </button>
        </div>

        <div className="mt-5" data-testid="panel-table">
          {tab === "leads" ? (
            <Table rows={leads} columns={leadCols} />
          ) : (
            <Table rows={onboarding} columns={onbCols} />
          )}
        </div>
      </main>
    </div>
  );
};

export default function Panel() {
  const [authenticated, setAuthenticated] = useState(null);

  useEffect(() => {
    adminSession().then((session) => setAuthenticated(Boolean(session.authenticated))).catch(() => setAuthenticated(false));
  }, []);

  const logout = useCallback(async () => {
    try { await adminLogout(); } catch { /* The local session still closes. */ }
    setAuthenticated(false);
  }, []);

  if (authenticated === null) return <div data-testid="panel-session-check" className="flex min-h-screen items-center justify-center bg-ink font-mono text-xs uppercase tracking-widest text-cream/70">Comprobando acceso...</div>;
  if (!authenticated) return <LoginView onLogin={() => setAuthenticated(true)} />;
  return <Dashboard onLogout={logout} />;
}
